import { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Form, Modal, Row, Spinner, Table } from 'react-bootstrap';
import axios from 'axios';
import { toast } from 'react-toastify';
import OrgPrintChrome from './OrgPrintChrome';

const errorMessage = (error, fallback) => {
  const data = error.response?.data;
  return typeof data === 'string' && data ? data : fallback;
};
const day = (d) => (d ? new Date(`${String(d).slice(0, 10)}T00:00:00`).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '');
const num = (v) => Number(v || 0);
const MSME = { MICRO: 'Micro', SMALL: 'Small', MEDIUM: 'Medium' };

/**
 * Accounts > Payables: what the business owes each vendor, worked out from the books, split into overdue, due within a
 * week and later, with each vendor's open bills and their due dates. Bills to micro and small enterprises are flagged
 * as they near 45 days.
 */
function Payables({ active, currency = 'INR', onPay, reloadKey, onLedger }) {
  const [data, setData] = useState(null);
  const [vendorType, setVendorType] = useState('');
  const [onlyDue, setOnlyDue] = useState(false);
  const [statement, setStatement] = useState(null);

  const money = useMemo(() => {
    const f = new Intl.NumberFormat('en-IN', { style: 'currency', currency, minimumFractionDigits: 2 });
    return (v) => f.format(num(v));
  }, [currency]);

  const load = useCallback(() => {
    axios.get('/api/accounts/payables')
      .then((r) => setData(r.data))
      .catch((error) => { setData({ vendors: [] }); toast.error(errorMessage(error, 'Could not work out payables')); });
  }, []);

  useEffect(() => { if (active) load(); }, [active, load, reloadKey]);

  const openStatement = async (vendorId) => {
    try {
      setStatement((await axios.get(`/api/accounts/payables/vendors/${vendorId}`)).data);
    } catch (error) {
      toast.error(errorMessage(error, 'Could not open the vendor’s bills'));
    }
  };

  if (!active) return null;
  if (!data) return <div className="d-flex justify-content-center py-4"><Spinner animation="border" /></div>;

  const types = [...new Set((data.vendors || []).map((v) => v.vendorType).filter(Boolean))].sort();
  const rows = (data.vendors || [])
    .filter((v) => !vendorType || v.vendorType === vendorType)
    .filter((v) => !onlyDue || num(v.overdue) > 0 || num(v.dueSoon) > 0);

  return (
    <div>
      <Row className="g-3 mb-3">
        {[
          ['Owed to vendors', data.totalOwed, ''],
          ['Overdue', data.overdue, num(data.overdue) > 0 ? 'text-danger' : ''],
          ['Due in the next 7 days', data.dueSoon, num(data.dueSoon) > 0 ? 'text-warning' : '']
        ].map(([label, value, cls]) => (
          <Col md={4} key={label}>
            <Card className="text-center h-100">
              <Card.Body className="py-2">
                <div className={`fs-4 fw-semibold ${cls}`}>{money(value)}</div>
                <div className="small text-muted">{label}</div>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>
      {data.msmeBillsAtRisk > 0 && (
        <Alert variant="warning" className="py-2 small">
          <i className="bi bi-exclamation-triangle me-1" />
          {data.msmeBillsAtRisk} bill{data.msmeBillsAtRisk === 1 ? '' : 's'} from micro or small enterprises must be paid within 45 days of the
          bill; paid later, the amount can&apos;t be claimed as an expense for income tax. Look for the MSME badge below.
        </Alert>
      )}

      <div className="d-flex flex-wrap align-items-center gap-3 mb-2">
        {types.length > 1 && (
          <Form.Select size="sm" style={{ width: 'auto' }} value={vendorType} onChange={(e) => setVendorType(e.target.value)}>
            <option value="">All vendor types</option>
            {types.map((t) => <option key={t} value={t}>{t}</option>)}
          </Form.Select>
        )}
        <Form.Check type="switch" id="payables-due" label="Only overdue or due this week" checked={onlyDue} onChange={(e) => setOnlyDue(e.target.checked)} />
      </div>

      <Table striped bordered hover responsive size="sm">
        <thead>
          <tr>
            <th>Vendor</th>
            <th className="text-end">Owed</th>
            <th className="text-end">Overdue</th>
            <th className="text-end">Due in 7 days</th>
            <th className="text-end">Later</th>
            <th className="text-end">Not on a bill</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr><td colSpan={7} className="text-center text-muted py-3">Nothing owed{onlyDue || vendorType ? ' here' : ' to vendors'}.</td></tr>
          )}
          {rows.map((v) => (
            <tr key={v.vendorId}>
              <td>
                <Button variant="link" className="p-0 text-start" onClick={() => openStatement(v.vendorId)}>{v.name}</Button>
                {v.msmeCategory && <Badge bg={v.msmeBillsAtRisk > 0 ? 'warning' : 'secondary'} text={v.msmeBillsAtRisk > 0 ? 'dark' : undefined} className="ms-2">MSME {MSME[v.msmeCategory]}</Badge>}
                <div className="small text-muted">
                  {[v.vendorType, v.openBills > 0 ? `${v.openBills} open bill${v.openBills === 1 ? '' : 's'}` : null,
                    v.creditDays != null ? `${v.creditDays} days credit` : null].filter(Boolean).join(' · ')}
                </div>
              </td>
              <td className="text-end text-nowrap fw-semibold">
                {num(v.advance) > 0 ? <span className="text-success">{money(v.advance)} paid ahead</span> : money(v.owed)}
              </td>
              <td className={`text-end text-nowrap ${num(v.overdue) > 0 ? 'text-danger' : 'text-muted'}`}>{num(v.overdue) > 0 ? money(v.overdue) : '-'}</td>
              <td className="text-end text-nowrap">{num(v.dueSoon) > 0 ? money(v.dueSoon) : '-'}</td>
              <td className="text-end text-nowrap">{num(v.later) > 0 ? money(v.later) : '-'}</td>
              <td className="text-end text-nowrap text-muted">{num(v.notOnBill) > 0 ? money(v.notOnBill) : '-'}</td>
              <td className="text-end text-nowrap">
                {num(v.owed) > 0 && onPay && <Button size="sm" variant="outline-primary" onClick={() => onPay(v.vendorId)}>Pay</Button>}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
      <p className="small text-muted">
        Worked out from the books, so the totals agree with the Balance Sheet. Payments that didn&apos;t name a bill settle the oldest bills
        first. &ldquo;Not on a bill&rdquo; is owed for goods received without a bill, or from the opening balances.
      </p>

      {statement && (
        <VendorStatement statement={statement} money={money} onHide={() => setStatement(null)}
          onPay={onPay ? () => { onPay(statement.vendorId); setStatement(null); } : null}
          onLedger={onLedger ? () => { onLedger(statement.vendorId); setStatement(null); } : null} />
      )}
    </div>
  );
}

function VendorStatement({ statement: s, money, onHide, onPay, onLedger }) {
  const today = new Date();
  const soon = new Date(); soon.setDate(today.getDate() + 7);
  return (
    <Modal show onHide={onHide} size="lg" dialogClassName="print-modal">
      <Modal.Header closeButton className="print-header">
        <Modal.Title as="h5">{s.name}: bills to pay</Modal.Title>
      </Modal.Header>
      <Modal.Body className="print-body">
        <div className="print-actions d-flex flex-wrap gap-2 mb-3">
          {onPay && num(s.owed) > 0 && <Button size="sm" onClick={onPay}>Record a payment</Button>}
          <Button size="sm" variant="outline-secondary" onClick={() => window.print()}><i className="bi bi-printer me-1" />Print</Button>
          {onLedger && <Button size="sm" variant="outline-secondary" onClick={onLedger}>Ledger statement</Button>}
        </div>
        <OrgPrintChrome>
          <div className="print-content">
            <div className="d-flex flex-wrap justify-content-between gap-2 mb-2">
              <div>
                <div className="fw-semibold">{s.name}</div>
                <div className="small text-muted">
                  {[s.creditDays != null ? `${s.creditDays} days credit` : 'No credit period set',
                    s.msmeCategory ? `MSME ${MSME[s.msmeCategory]}${s.udyamNumber ? ` (${s.udyamNumber})` : ''}` : null].filter(Boolean).join(' · ')}
                </div>
              </div>
              <div className="text-end">
                <div className="small text-muted">Owed as on {day(s.asOf)}</div>
                <div className="fs-5 fw-bold">{num(s.advance) > 0 ? `${money(s.advance)} paid ahead` : money(s.owed)}</div>
              </div>
            </div>
            <Table bordered size="sm" className="small">
              <thead>
                <tr>
                  <th>Bill</th><th>Date</th><th>Due</th><th className="text-end">Amount</th>
                  <th className="text-end">Paid / returned</th><th className="text-end">Outstanding</th>
                </tr>
              </thead>
              <tbody>
                {s.bills.length === 0 && <tr><td colSpan={6} className="text-center text-muted">No open bills.</td></tr>}
                {s.bills.map((b) => {
                  const due = new Date(`${b.dueDate}T00:00:00`);
                  const payBy = b.msmePayBy ? new Date(`${b.msmePayBy}T00:00:00`) : null;
                  return (
                    <tr key={b.id}>
                      <td>
                        {b.vendorInvoiceNumber || b.internalNumber}
                        {b.documentKind === 'NON_GST' && <Badge bg="light" text="dark" className="ms-1 border">slip</Badge>}
                        <div className="text-muted">{b.internalNumber}</div>
                      </td>
                      <td className="text-nowrap">{day(b.invoiceDate)}</td>
                      <td className="text-nowrap">
                        {day(b.dueDate)}
                        {b.daysOverdue > 0 ? <div className="text-danger">{b.daysOverdue} days overdue</div>
                          : due <= soon ? <div className="text-warning">due this week</div> : null}
                        {payBy && <div className={payBy <= soon ? 'text-danger' : 'text-muted'}>MSME: pay by {day(b.msmePayBy)}</div>}
                      </td>
                      <td className="text-end text-nowrap">{money(b.amount)}</td>
                      <td className="text-end text-nowrap">{num(b.paid) + num(b.returned) > 0 ? money(num(b.paid) + num(b.returned)) : '-'}</td>
                      <td className="text-end text-nowrap fw-semibold">{money(b.outstanding)}</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                {num(s.notOnBill) > 0 && (
                  <tr><td colSpan={5}>Owed but not on a bill (goods received without a bill, opening balance)</td><td className="text-end">{money(s.notOnBill)}</td></tr>
                )}
                <tr className="fw-bold"><td colSpan={5} className="text-end">Total owed</td><td className="text-end">{money(Math.max(0, num(s.owed)))}</td></tr>
              </tfoot>
            </Table>
          </div>
        </OrgPrintChrome>
      </Modal.Body>
    </Modal>
  );
}

export default Payables;
